import type {
  ContextByTypeAndId,
  TreeHierarchyCheck,
  TreeHierarchyContext,
  TreeHierarchyWorkflow,
  TreeHierarchyItem,
} from '../types'
import type {
  CheckContext,
  CheckPayload,
  WorkflowListItem,
} from '@app/shared/types/sf-data'
import { SilverfinContextCamelCase } from '@app/shared/types/sf-data'

import omit from 'lodash/omit'

import TreeNode from '@app/utils/tree'

export function generateChecksTreeHierarchy({
  checksResponse,
  workflowId,
  workflows = [],
}: {
  checksResponse: CheckPayload | undefined
  workflowId: number | null
  workflows: WorkflowListItem[] | undefined
}): TreeNode<TreeHierarchyItem> {
  if (!checksResponse) {
    return TreeNode.of<TreeHierarchyItem>(
      {
        id: '1',
        name: 'root',
        type: 'group',
        children: [],
      },
      []
    )
  }

  const contextByTypeById = getContextByTypeAndId(checksResponse.contexts)

  const scopedWorkflows: WorkflowListItem[] = workflowId
    ? workflows.filter((workflow) => workflow.id === workflowId)
    : workflows

  const workflowsToReduce: WorkflowListItem[] = workflowId
    ? scopedWorkflows
    : [
        ...scopedWorkflows,
        {
          id: 1,
          default: false,
          name: 'General',
        } as WorkflowListItem,
      ]

  // Workflow membership comes from the workflows/list endpoint, which reads the
  // reconciliation report copies without refreshing them, so it can lag behind the
  // checks response. Contexts matched by no workflow would silently drop out of the
  // tree; adopt them into the scoped workflow or the default workflow, like the
  // inbox grouping does. Two deliberate consequences:
  // - In scoped mode the checks response is built server-side from the fresh,
  //   workflow-scoped report, so it is authoritative: everything in it renders under
  //   the scoped workflow even when the stale membership list disagrees.
  // - The list endpoint also omits archived workflows, so contexts owned only by
  //   archived workflows count as orphaned permanently, not just while stale: their
  //   checks surface under the default workflow (before, they were hidden from the
  //   tree entirely) and mark-all-as-read on that node includes them.
  const workflowIncludesContext = (
    workflow: WorkflowListItem,
    contextType: SilverfinContextCamelCase,
    contextId: number
  ): boolean =>
    contextType === SilverfinContextCamelCase.Account
      ? workflow.accountIds.includes(contextId)
      : workflow.reconciliationTextIds.includes(contextId)

  const scopedAccountIds = new Set(
    scopedWorkflows.flatMap((workflow) => workflow.accountIds)
  )
  const scopedReconciliationTextIds = new Set(
    scopedWorkflows.flatMap((workflow) => workflow.reconciliationTextIds)
  )

  const contextIsOrphaned = (
    contextType: SilverfinContextCamelCase,
    contextId: number
  ): boolean =>
    contextType === SilverfinContextCamelCase.Account
      ? !scopedAccountIds.has(contextId)
      : !scopedReconciliationTextIds.has(contextId)

  const orphanAdopterId = workflowId
    ? workflowId
    : scopedWorkflows.find((workflow) => workflow.default)?.id

  const treeHierarchyStructure = workflowsToReduce
    .reduce((workflowAccumulator, workflow) => {
      if (workflow.name === 'General') {
        // Insert a dummy top-level node for the general category
        workflowAccumulator.push({
          ...workflow,
          id: 'general',
          type: 'workflow',
          children: checksResponse.general.map((check) =>
            Object.freeze({
              ...check,
              contextUrl: '',
              contextUrlText: '',
              type: 'check',
            } as TreeHierarchyCheck)
          ),
        })
      } else {
        const includeContext =
          (contextType: SilverfinContextCamelCase) =>
          (contextId: number): boolean =>
            workflowIncludesContext(workflow, contextType, contextId) ||
            (workflow.id === orphanAdopterId &&
              contextIsOrphaned(contextType, contextId))

        workflowAccumulator.push(
          Object.freeze({
            ...omit(workflow, ['accountIds', 'reconciliationTextIds']),
            id: workflow.id.toString(),
            type: 'workflow',
            children: [
              ...Object.keys(checksResponse.reconciliations).reduce(
                accumulateContext({
                  checksResponse,
                  contextByTypeById,
                  contextType: SilverfinContextCamelCase.ReconciliationText,
                  includeContext: includeContext(
                    SilverfinContextCamelCase.ReconciliationText
                  ),
                }),
                [] as TreeHierarchyContext[]
              ),
              ...Object.keys(checksResponse.accounts).reduce(
                accumulateContext({
                  checksResponse,
                  contextByTypeById,
                  contextType: SilverfinContextCamelCase.Account,
                  includeContext: includeContext(
                    SilverfinContextCamelCase.Account
                  ),
                }),
                [] as TreeHierarchyContext[]
              ),
            ].sort(contextSorter),
          })
        )
      }

      return workflowAccumulator
    }, [] as TreeHierarchyWorkflow[])
    .sort(workflowSorter)

  const tree = TreeNode.of<TreeHierarchyItem>(
    {
      id: '1',
      name: 'root',
      type: 'group',
      children: [],
    },
    treeHierarchyStructure
  )

  return tree
}

const getContextByTypeAndId = (
  contexts: CheckContext[]
): ContextByTypeAndId => {
  return contexts.reduce<ContextByTypeAndId>(
    (acc, context) => {
      if (context.type === SilverfinContextCamelCase.Account) {
        acc.accounts[context.id] = context
      } else {
        acc.reconciliations[context.id] = context
      }

      return acc
    },
    {
      accounts: {},
      reconciliations: {},
    }
  )
}

const accumulateContext =
  ({
    checksResponse,
    contextByTypeById,
    contextType,
    includeContext,
  }: {
    checksResponse: CheckPayload
    contextByTypeById: ContextByTypeAndId
    contextType: SilverfinContextCamelCase
    includeContext: (contextId: number) => boolean
  }) =>
  (
    accumulator: TreeHierarchyContext[],
    contextIdString: string
  ): TreeHierarchyContext[] => {
    const numberContextId = parseInt(contextIdString, 10)

    if (!includeContext(numberContextId)) {
      return accumulator
    }

    const context =
      contextByTypeById[
        contextType === SilverfinContextCamelCase.Account
          ? 'accounts'
          : 'reconciliations'
      ][numberContextId]

    if (!context) {
      return accumulator
    }

    const contextualChecks =
      contextType === SilverfinContextCamelCase.Account
        ? checksResponse.accounts[numberContextId]
        : checksResponse.reconciliations[numberContextId]

    accumulator.push({
      ...context,
      id: getComplexContextIdByTypeAndId(context.type, context.id),
      children: contextualChecks.map((check) =>
        Object.freeze({
          ...check,
          contextUrl: context.url,
          contextUrlText: context.name,
          type: 'check',
        } as TreeHierarchyCheck)
      ),
    })

    return accumulator
  }

function getComplexContextIdByTypeAndId(
  type:
    | SilverfinContextCamelCase.Account
    | SilverfinContextCamelCase.ReconciliationText,
  contextId: number
): string {
  if (type === SilverfinContextCamelCase.Account) {
    return `account__${contextId}`
  } else if (type === SilverfinContextCamelCase.ReconciliationText) {
    return `reconciliation__${contextId}`
  }

  return `${contextId}`
}

const contextSorter = (
  a: TreeHierarchyContext,
  b: TreeHierarchyContext
): number => a.name.localeCompare(b.name)

const workflowSorter = (
  a: TreeHierarchyWorkflow,
  b: TreeHierarchyWorkflow
): number => {
  if (a.default) {
    return -1
  } else if (b.default) {
    return 1
  }
  return a.name.localeCompare(b.name)
}
